import React, { useEffect, useMemo, useRef, useState } from 'react';
import Hls from 'hls.js';
import { mediaTimeToSessionElapsed, sessionElapsedToMediaTime } from '../core/mediaClock.js';

const MAX_PLAYBACK_RATE = 16;

/** Les tags ID3 du muxer (ROTATE:<deg> TS:<epoch ms>) relient le média à l'heure réelle. */
function cuesFromId3(data) {
    const frag = data?.frag;
    const fragStart = Number.isFinite(frag?.start) ? frag.start : 0;
    const fragEnd = fragStart + (Number.isFinite(frag?.duration) ? frag.duration : 10);
    const cues = [];
    (data?.samples || []).forEach(sample => {
        if (!sample?.data) return;
        let text = '';
        try { text = new TextDecoder('utf-8').decode(sample.data); } catch (_) { return; }
        const rot = text.match(/ROTATE:(-?\d+(?:\.\d+)?)/i);
        const ts = text.match(/TS:(\d+)/i);
        if (!rot && !ts) return;
        // Certains flux donnent un PTS absolu : on retombe alors sur le début du fragment.
        const pts = Number.isFinite(sample.pts) && sample.pts >= fragStart - 1 && sample.pts <= fragEnd + 1
            ? sample.pts : fragStart;
        cues.push({
            pts,
            rot: rot ? ((parseFloat(rot[1]) % 360) + 360) % 360 : null,
            ts: ts ? parseInt(ts[1], 10) : null,
        });
    });
    return cues;
}

function rotationAt(cues, mediaTime) {
    let rotation = 0;
    for (const cue of cues) {
        if (cue.pts > mediaTime) break;
        if (cue.rot !== null) rotation = cue.rot;
    }
    return rotation;
}

function drawRotated(canvas, video, degrees) {
    const ctx = canvas?.getContext('2d');
    if (!ctx || !video?.videoWidth || !video?.videoHeight) return;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const rad = (degrees * Math.PI) / 180;
    const width = Math.round(vw * Math.abs(Math.cos(rad)) + vh * Math.abs(Math.sin(rad)));
    const height = Math.round(vw * Math.abs(Math.sin(rad)) + vh * Math.abs(Math.cos(rad)));
    if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
    }
    ctx.clearRect(0, 0, width, height);
    ctx.save();
    ctx.translate(width / 2, height / 2);
    ctx.rotate(rad);
    ctx.drawImage(video, -vw / 2, -vh / 2, vw, vh);
    ctx.restore();
}

/**
 * Lecteur média synchronisé : il suit l'horloge de l'activité, et en devient
 * la référence tant qu'il joue (l'heure vient alors des tags ID3 du flux).
 */
export default function VideoPane({
    bundle, clock, hlsConfig,
    // Pilotage externe (vue enquête) : source et volume choisis ailleurs, état remonté pour la timeline.
    sourceId: controlledSourceId = null, volume: controlledVolume = null, hideControls = false, onState = null,
    // 'locked' : redressement par quart de tour (comme la relecture des sessions) ; 'free' : angle exact.
    rotationMode = 'free',
}) {
    const sources = useMemo(() => (bundle?.tracks || []).flatMap(track =>
        (track.media || []).map(media => ({ ...media, trackId: track.id, color: track.color }))), [bundle]);
    const [ownSourceId, setSourceId] = useState(() => (sources.find(s => s.type === 'video') || sources[0])?.id || null);
    const sourceId = controlledSourceId || ownSourceId;
    const [covered, setCovered] = useState(false);
    const [muted, setMuted] = useState(false);
    const [ownVolume, setVolume] = useState(1);
    const volume = controlledVolume ?? ownVolume;
    const driverFnRef = useRef(null);
    const onStateRef = useRef(onState);
    onStateRef.current = onState;
    const rotationModeRef = useRef(rotationMode);
    rotationModeRef.current = rotationMode;
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const cuesRef = useRef([]);
    const source = sources.find(s => s.id === sourceId) || null;
    const startMs = bundle?.startMs ?? 0;

    useEffect(() => {
        if (!sources.some(s => s.id === sourceId)) {
            setSourceId((sources.find(s => s.type === 'video') || sources[0])?.id || null);
        }
    }, [sources, sourceId]);

    const fallbackOffset = source ? Math.max(0, (source.startMs - startMs) / 1000) : 0;
    const toMediaTime = epochMs => sessionElapsedToMediaTime({
        sessionElapsed: (epochMs - startMs) / 1000, cues: cuesRef.current, sessionStartTs: startMs, fallbackOffset,
    });
    const toEpoch = mediaTime => startMs + 1000 * mediaTimeToSessionElapsed({
        mediaTime, cues: cuesRef.current, sessionStartTs: startMs, fallbackOffset,
    });
    const mappingRef = useRef({ toMediaTime, toEpoch });
    mappingRef.current = { toMediaTime, toEpoch };

    // Chargement du flux HLS de la source choisie.
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !source) return undefined;
        cuesRef.current = [];
        let hls = null;
        if (Hls.isSupported()) {
            let sourceConfig = hlsConfig || {};
            if (source.mediaKey) {
                const DefaultLoader = Hls.DefaultConfig.loader;
                class AlertKeyLoader extends DefaultLoader {
                    load(context, loaderConfig, callbacks) {
                        if (/\/(?:cle|key)\.key(?:\?|$)/.test(context.url)) {
                            const data = source.mediaKey.slice().buffer;
                            callbacks.onSuccess({ url: context.url, data },
                                { trequest: 0, tfirst: 0, tload: 0, loaded: data.byteLength, total: data.byteLength }, context, null);
                            return;
                        }
                        super.load(context, loaderConfig, callbacks);
                    }
                }
                sourceConfig = { ...sourceConfig, loader: AlertKeyLoader,
                    xhrSetup: xhr => Object.entries(source.mediaHeaders || {}).forEach(([name, value]) => xhr.setRequestHeader(name, value)) };
            }
            hls = new Hls({ enableWorker: true, ...sourceConfig });
            hls.on(Hls.Events.FRAG_PARSING_METADATA, (_event, data) => {
                const cues = cuesFromId3(data);
                if (cues.length === 0) return;
                cuesRef.current = [...cuesRef.current, ...cues].sort((a, b) => a.pts - b.pts);
            });
            hls.on(Hls.Events.ERROR, (_event, data) => {
                if (!data.fatal) return;
                if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
                else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
            });
            hls.loadSource(source.playlistUrl);
            hls.attachMedia(video);
        } else {
            // Sans MSE, impossible d'ajouter l'authentification aux requêtes.
            video.removeAttribute('src');
        }
        return () => {
            if (driverFnRef.current) { (clock.releaseDriver || (() => clock.setDriver(null)))(driverFnRef.current); driverFnRef.current = null; }
            if (hls) hls.destroy();
            video.pause();
            video.removeAttribute('src');
            video.load();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source?.id, source?.playlistUrl, hlsConfig]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        video.muted = muted;
        video.volume = volume;
    }, [muted, volume]);

    // Suivi de l'horloge : lecture, pause et rattrapage quand la vidéo couvre l'instant.
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !source) return undefined;
        const tick = () => {
            const { toMediaTime: mediaAt } = mappingRef.current;
            const mediaTime = mediaAt(clock.tRef.current);
            const duration = Number.isFinite(video.duration) ? video.duration : null;
            const isCovered = Number.isFinite(mediaTime) && mediaTime >= 0 && (duration === null || mediaTime <= duration);
            setCovered(isCovered);
            onStateRef.current?.({
                sourceId: source.id, type: source.type, covered: isCovered,
                position: Number.isFinite(mediaTime) ? Math.max(0, mediaTime) : 0, duration: duration || 0,
                startMs: source.startMs, endMs: source.endMs, videoWidth: video.videoWidth || 0, videoHeight: video.videoHeight || 0,
            });
            if (clock.playing && isCovered) {
                const rate = Math.min(MAX_PLAYBACK_RATE, clock.speed);
                if (video.playbackRate !== rate) video.playbackRate = rate;
                if (video.paused) {
                    if (Math.abs(video.currentTime - mediaTime) > 0.5) video.currentTime = mediaTime;
                    video.play().catch(() => {});
                }
                // Un seul lecteur mène l'horloge : le premier qui joue ; les autres le suivent.
                // Lecteur suiveur : il se recale s'il dérive de plus d'une seconde.
                if (!driverFnRef.current && clock.hasDriver?.() && !video.paused && Math.abs(video.currentTime - mediaTime) > 1) {
                    video.currentTime = mediaTime;
                }
                if (!driverFnRef.current && !clock.hasDriver?.()) {
                    driverFnRef.current = () => (video.paused || video.seeking ? null : mappingRef.current.toEpoch(video.currentTime));
                    clock.setDriver(driverFnRef.current);
                }
            } else {
                if (driverFnRef.current) { (clock.releaseDriver || (() => clock.setDriver(null)))(driverFnRef.current); driverFnRef.current = null; }
                if (!video.paused) video.pause();
            }
        };
        tick();
        const interval = setInterval(tick, 250);
        return () => clearInterval(interval);
    }, [clock, clock.playing, clock.speed, source]);

    // Saut manuel : la vidéo se repositionne immédiatement.
    useEffect(() => clock.onSeek(epochMs => {
        const video = videoRef.current;
        if (!video) return;
        const mediaTime = mappingRef.current.toMediaTime(epochMs);
        if (Number.isFinite(mediaTime) && mediaTime >= 0) video.currentTime = mediaTime;
    }), [clock]);

    // Rendu de l'image tournée selon les tags ROTATE.
    useEffect(() => {
        if (!source || source.type !== 'video') return undefined;
        let frame = null;
        const render = () => {
            const video = videoRef.current;
            const degrees = rotationAt(cuesRef.current, video?.currentTime || 0);
            if (video) drawRotated(canvasRef.current, video, rotationModeRef.current === 'locked' ? (Math.round(degrees / 90) * 90) % 360 : degrees);
            frame = requestAnimationFrame(render);
        };
        frame = requestAnimationFrame(render);
        return () => cancelAnimationFrame(frame);
    }, [source]);

    if (sources.length === 0) {
        return <div className="av-video av-empty">Aucune vidéo ni aucun audio pour cette activité.</div>;
    }

    return (
        <div className="av-video">
            {!hideControls && <div className="av-video-header">
                <select value={sourceId || ''} onChange={event => setSourceId(event.target.value)} className="av-select">
                    {sources.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
                <button type="button" className="av-button" onClick={() => setMuted(!muted)}>{muted ? 'Son coupé' : 'Son'}</button>
                <input
                    type="range" min="0" max="1" step="0.05" value={volume}
                    onChange={event => setVolume(Number(event.target.value))}
                    aria-label="Volume"
                />
            </div>}
            <div className="av-video-stage">
                <video ref={videoRef} playsInline className="av-video-element" />
                {source?.type === 'video'
                    ? <canvas ref={canvasRef} className="av-video-canvas" />
                    : <div className="av-audio-visual" style={{ borderColor: source?.color }}>Audio en cours</div>}
                {!covered && <div className="av-video-overlay">Pas d'enregistrement à cet instant</div>}
            </div>
        </div>
    );
}
